/**
 * GDPR Compliance and Data Protection Utilities
 * Implements security and privacy features according to RGPD (GDPR)
 */

// Data categories for GDPR compliance
export type DataCategory =
    | "personal_identification"
    | "contact_information"
    | "financial_data"
    | "service_history"
    | "technical_data"
    | "location_data"
    | "communication_data";

// Legal basis for data processing
export type LegalBasis =
    | "consent"
    | "contract"
    | "legal_obligation"
    | "vital_interests"
    | "public_task"
    | "legitimate_interests";

// Data subject rights under GDPR
export type DataSubjectRight =
    | "access"
    | "rectification"
    | "erasure"
    | "restriction"
    | "portability"
    | "objection"
    | "automated_decision";

// Consent record
export interface ConsentRecord {
    id: string;
    userId: string;
    purpose: string;
    categories: DataCategory[];
    legalBasis: LegalBasis;
    consentGiven: boolean;
    consentDate: string;
    expiryDate?: string;
    withdrawnDate?: string;
    ipAddress: string;
    userAgent: string;
}

// Data processing activity
export interface ProcessingActivity {
    id: string;
    name: string;
    purpose: string;
    dataCategories: DataCategory[];
    legalBasis: LegalBasis;
    retention: string; // e.g., "5 years", "until contract end"
    recipients?: string[]; // Who receives the data
    thirdCountryTransfers?: boolean;
}

// Audit log entry
export interface AuditLog {
    id: string;
    timestamp: string;
    userId: string;
    userName: string;
    action: string;
    resource: string;
    resourceId: string;
    details?: Record<string, unknown>;
    ipAddress: string;
    success: boolean;
}

// Data breach incident
export interface DataBreachIncident {
    id: string;
    reportedDate: string;
    discoveredDate: string;
    description: string;
    affectedDataCategories: DataCategory[];
    affectedUsers: number;
    severity: "low" | "medium" | "high" | "critical";
    notificationsSent: boolean;
    authoritiesNotified: boolean;
    mitigationSteps: string[];
    status: "open" | "investigating" | "resolved";
}

/**
 * Encrypt sensitive data (placeholder - use proper encryption in production)
 */
export function encryptData(data: string): string {
    // In production: use proper encryption like AES-256
    // This is a placeholder for demonstration
    return btoa(data); // Base64 is NOT encryption, just for demo
}

/**
 * Decrypt sensitive data (placeholder - use proper decryption in production)
 */
export function decryptData(encryptedData: string): string {
    // In production: use proper decryption
    try {
        return atob(encryptedData);
    } catch {
        return "";
    }
}

/**
 * Hash sensitive data (one-way)
 */
export function hashData(data: string): string {
    // In production: use proper hashing like SHA-256
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
        const char = data.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
    }
    return hash.toString(16);
}

/**
 * Anonymize personal data
 */
export function anonymizeData(data: string): string {
    // Replace with asterisks, keeping first and last characters
    if (data.length <= 2) return "***";
    return data[0] + "*".repeat(data.length - 2) + data[data.length - 1];
}

/**
 * Pseudonymize personal data (reversible with key)
 */
export function pseudonymizeData(data: string, userId: string): string {
    // In production: use proper pseudonymization
    return `PSEUDO_${hashData(data + userId)}`;
}

/**
 * Check if data retention period has expired
 */
export function isRetentionExpired(
    createdDate: string,
    retentionYears: number
): boolean {
    const created = new Date(createdDate);
    const expiry = new Date(created);
    expiry.setFullYear(expiry.getFullYear() + retentionYears);
    return new Date() > expiry;
}

/**
 * Calculate data retention expiry date
 */
export function calculateRetentionExpiry(
    startDate: string,
    retentionYears: number
): string {
    const start = new Date(startDate);
    const expiry = new Date(start);
    expiry.setFullYear(expiry.getFullYear() + retentionYears);
    return expiry.toISOString();
}

/**
 * Validate consent is still valid
 */
export function isConsentValid(consent: ConsentRecord): boolean {
    if (!consent.consentGiven) return false;
    if (consent.withdrawnDate) return false;
    if (consent.expiryDate && new Date(consent.expiryDate) < new Date())
        return false;
    return true;
}

/**
 * Create audit log entry
 */
export function createAuditLog(
    userId: string,
    userName: string,
    action: string,
    resource: string,
    resourceId: string,
    success: boolean,
    details?: Record<string, unknown>
): AuditLog {
    return {
        id: `AUDIT-${Date.now()}`,
        timestamp: new Date().toISOString(),
        userId,
        userName,
        action,
        resource,
        resourceId,
        details,
        ipAddress: "0.0.0.0", // In production: get real IP
        success,
    };
}

/**
 * Sanitize input to prevent XSS attacks
 */
export function sanitizeInput(input: string): string {
    return input
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#x27;")
        .replace(/\//g, "&#x2F;");
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Validate Spanish NIF/CIF format
 */
export function isValidNIF(nif: string): boolean {
    const nifRegex = /^[0-9]{8}[A-Z]$/;
    const cifRegex = /^[A-Z][0-9]{7}[A-Z0-9]$/;
    return nifRegex.test(nif) || cifRegex.test(nif);
}

/**
 * Generate privacy policy text
 */
export function getPrivacyPolicyText(): string {
    return `
POLÍTICA DE PRIVACIDAD Y PROTECCIÓN DE DATOS

1. RESPONSABLE DEL TRATAMIENTO
BIOSAM - Control de Plagas
NIF: B12345678
Dirección: Calle Ejemplo 123, 28001 Madrid
Email: privacidad@biosam.com

2. FINALIDAD DEL TRATAMIENTO
Tus datos personales serán tratados para:
- Gestión de servicios de control de plagas
- Emisión de facturas y documentación legal
- Comunicaciones relacionadas con el servicio
- Cumplimiento de obligaciones legales

3. BASE LEGAL
- Ejecución de contrato de servicios
- Consentimiento del interesado
- Cumplimiento de obligaciones legales

4. CONSERVACIÓN DE DATOS
Los datos se conservarán durante:
- Duración del contrato + 5 años (obligaciones fiscales)
- Datos de salud: hasta finalización del tratamiento
- Consentimientos: hasta su retirada

5. DERECHOS DEL INTERESADO
Tienes derecho a:
- Acceso a tus datos
- Rectificación de datos inexactos
- Supresión ("derecho al olvido")
- Limitación del tratamiento
- Portabilidad de datos
- Oposición al tratamiento
- No ser objeto de decisiones automatizadas

Para ejercer tus derechos: privacidad@biosam.com

6. SEGURIDAD
Implementamos medidas técnicas y organizativas:
- Cifrado de datos en tránsito (TLS 1.3)
- Cifrado de datos en reposo (AES-256)
- Control de acceso basado en roles
- Auditorías de seguridad regulares
- Formación del personal en protección de datos

7. TRANSFERENCIAS INTERNACIONALES
No se realizan transferencias fuera del EEE sin garantías adecuadas.

8. RECLAMACIONES
Puedes presentar reclamación ante la Agencia Española de Protección de Datos (AEPD).
  `.trim();
}

/**
 * Get data processing activities
 */
export function getProcessingActivities(): ProcessingActivity[] {
    return [
        {
            id: "PA-001",
            name: "Gestión de clientes",
            purpose: "Administración de relaciones comerciales",
            dataCategories: [
                "personal_identification",
                "contact_information",
                "location_data",
            ],
            legalBasis: "contract",
            retention: "5 años tras fin del contrato",
            recipients: ["Personal autorizado", "Asesoría fiscal"],
        },
        {
            id: "PA-002",
            name: "Facturación",
            purpose: "Emisión y gestión de facturas",
            dataCategories: ["personal_identification", "financial_data"],
            legalBasis: "legal_obligation",
            retention: "10 años (obligación fiscal)",
            recipients: ["Agencia Tributaria", "Asesoría fiscal"],
        },
        {
            id: "PA-003",
            name: "Servicios de control de plagas",
            purpose: "Prestación del servicio contratado",
            dataCategories: [
                "personal_identification",
                "contact_information",
                "location_data",
                "service_history",
                "technical_data",
            ],
            legalBasis: "contract",
            retention: "Duración del contrato + 5 años",
            recipients: ["Técnicos", "Personal administrativo"],
        },
        {
            id: "PA-004",
            name: "Portal de clientes",
            purpose: "Acceso a documentación y servicios online",
            dataCategories: [
                "personal_identification",
                "contact_information",
                "technical_data",
                "communication_data",
            ],
            legalBasis: "consent",
            retention: "Hasta retirada del consentimiento",
            recipients: ["Proveedor de hosting"],
        },
    ];
}

export default {
    encryptData,
    decryptData,
    hashData,
    anonymizeData,
    pseudonymizeData,
    isRetentionExpired,
    calculateRetentionExpiry,
    isConsentValid,
    createAuditLog,
    sanitizeInput,
    isValidEmail,
    isValidNIF,
    getPrivacyPolicyText,
    getProcessingActivities,
};
